import { useEffect, useState } from "react";
import { useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { WorkoutForm } from "../WorkoutForm";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { clientLogTimes, dayStartMs, localDayStartMs } from "../../lib/dayHelpers";
import { useLoggingFlow } from "../../lib/logging-flow";
import type { LoggingFlowKind } from "../../lib/logging-flow";
import type { Screen } from "../nav/BottomNav";
import MealReview from "../../screens/MealReview";
import BarcodeScanner from "../../screens/BarcodeScanner";
import FoodSearch from "../../screens/FoodSearch";
import { TextLogFlow } from "../logging/TextLogFlow";
import { VoiceLogFlow } from "../logging/VoiceLogFlow";
import { PhotoLogFlow } from "../logging/PhotoLogFlow";
import { LabelScanFlow } from "../logging/LabelScanFlow";
import { MealForm } from "../MealForm";
import { toMealReviewPayload } from "../meal-review/review-utils";
import { FlowSheetShell } from "./FlowSheetShell";

// Interim flow-host routing (plan todo 10). The overlay layer sits above all
// content (z-40). Capture flow screens arrive in todos 13-18 and the unified
// Meal Review screen in todo 12; until then those kinds render a clearly
// labeled stub sheet so no launcher action is ever dead.
const FLOW_STUBS: Partial<Record<LoggingFlowKind, { readonly title: string; readonly todo: number }>> = {};

// Interim stub for flows whose screens land in todos 13-18. Clearly scoped:
// each stub names its owning todo and is replaced by that todo's real screen.
function FlowStubSheet({ kind, onClose }: {
  readonly kind: LoggingFlowKind;
  readonly onClose: () => void;
}) {
  const stub = FLOW_STUBS[kind];
  return (
    <FlowSheetShell title={stub?.title ?? "Log"} onClose={onClose} testId="flow-stub">
      <p className="text-sm text-text-muted" data-testid="flow-stub-message">
        {stub !== undefined
          ? `This logging flow is on its way (arrives with todo ${stub.todo}). Nothing to do here yet.`
          : "This logging flow is on its way."}
      </p>
      <Button
        variant="secondary"
        size="md"
        className="mt-4 w-full"
        data-testid="flow-stub-dismiss"
        onClick={onClose}
      >
        Got it
      </Button>
    </FlowSheetShell>
  );
}

// Weight sheet: reuses the exact Today weight-logging path (api.weight.log
// with the same optimistic update) so launcher weight logging matches the
// Today card behavior.
function WeightFlowSheet({ onClose }: { readonly onClose: () => void }) {
  const [weightInput, setWeightInput] = useState("");
  const [weightError, setWeightError] = useState<string | null>(null);
  const today = localDayStartMs(Date.now());

  const logWeight = useMutation(api.weight.log).withOptimisticUpdate((localStore, args) => {
    const weightQuery = { fromDate: args.date, toDate: args.date, clientFromDayStart: args.clientDayStart, clientToDayStart: args.clientDayStart };
    const current = localStore.getQuery(api.weight.list, weightQuery);
    if (current === undefined) return;
    const profile = localStore.getQuery(api.profile.getProfile, {});
    if (profile === undefined || profile === null) return;
    const now = Date.now();
    localStore.setQuery(api.weight.list, weightQuery, [
      ...current,
      {
        _id: `temp_${now}_${Math.random().toString(36).slice(2)}` as Id<"weightHistory">,
        _creationTime: now,
        userId: profile._id,
        date: args.clientDayStart ?? dayStartMs(args.date),
        weightKg: args.weightKg,
        loggedAt: args.clientLoggedAt ?? now,
        updatedAt: now,
      },
    ]);
  });

  const saveWeight = async () => {
    const value = Number(weightInput);
    if (!Number.isFinite(value) || value <= 0) {
      setWeightError("Enter a positive weight in kg.");
      return;
    }
    setWeightError(null);
    try {
      await logWeight({ date: today, weightKg: value, ...clientLogTimes(today) });
      console.log("[Frontend][LoggingFlow] Weight saved from quick log", { date: today });
      onClose();
    } catch (error: unknown) {
      console.error("[Frontend][LoggingFlow] Weight save failed", {
        error: error instanceof Error ? error.message : String(error),
      });
      setWeightError("Weight could not be saved. Try again.");
    }
  };

  return (
    <FlowSheetShell title="Log weight" onClose={onClose} testId="flow-weight">
      <div className="flex items-end gap-2">
        <Input
          label="Weight (kg)"
          type="number"
          inputMode="decimal"
          min="1"
          step="0.1"
          value={weightInput}
          onChange={(event) => { setWeightInput(event.target.value); setWeightError(null); }}
          placeholder="82.0"
        />
        <Button size="sm" className="shrink-0" data-testid="flow-weight-save" onClick={() => void saveWeight()}>
          Save
        </Button>
      </div>
      {weightError && <p className="mt-2 text-sm text-danger" role="alert">{weightError}</p>}
    </FlowSheetShell>
  );
}

export interface LoggingFlowHostProps {
  readonly demoMode: boolean;
  readonly onNavigate: (screen: Screen) => void;
}

export function LoggingFlowHost({ demoMode, onNavigate }: LoggingFlowHostProps) {
  const { activeFlow, closeFlow } = useLoggingFlow();
  const kind = activeFlow?.kind ?? null;

  // "library" is a navigation flow, not a sheet: route to the library screen
  // (Memory placeholder until todo 19) and clear the flow.
  useEffect(() => {
    if (kind !== "library") return;
    console.log("[Frontend][LoggingFlow] Routing library flow to library screen");
    onNavigate("library");
    closeFlow();
  }, [kind, onNavigate, closeFlow]);

  if (kind === null || kind === "library") return null;

  // Real screens reused from Today: WorkoutForm for exercise, the weight
  // logging path for weight. Demo mode has no backend, so these render the
  // same interim stub as the capture flows.
  if (!demoMode && kind === "exercise") {
    return (
      <FlowSheetShell title="Log exercise" onClose={closeFlow} testId="flow-exercise">
        <WorkoutForm date={localDayStartMs(Date.now())} onSaved={closeFlow} />
      </FlowSheetShell>
    );
  }

  if (!demoMode && kind === "weight") {
    return <WeightFlowSheet onClose={closeFlow} />;
  }

  // Barcode scanner (todo 16): full-screen live scanner with OFF lookup,
  // product card, and not-found/manual fallbacks. Demo mode scans for real
  // but resolves lookups to a local demo product — no network, no backend.
  if (kind === "barcode") {
    return <BarcodeScanner demoMode={demoMode} onClose={closeFlow} />;
  }

  // Food search (todo 17): full-screen tabbed search across the user's
  // library and the verified OFF database (submit-only). payload.query
  // pre-seeds the search field (barcode not-found fallback).
  if (!demoMode && kind === "search") {
    return <FoodSearch initialQuery={activeFlow?.payload?.query} onClose={closeFlow} />;
  }

  // Photo capture / library upload (todo 13). payload.file carries a photo
  // already chosen via the always-mounted native picker (photo-picker) — the
  // flow then skips its pick step and uploads immediately.
  if (kind === "photo" || kind === "upload") {
    return (
      <PhotoLogFlow
        kind={kind}
        demoMode={demoMode}
        onClose={closeFlow}
        initialFile={activeFlow?.payload?.file}
      />
    );
  }

  // Unified Meal Review (todo 12): full-screen; works in demo mode too (the
  // plate and review edits are local; Log meal is disabled while signed out).
  if (kind === "review") {
    return (
      <MealReview
        payload={toMealReviewPayload(activeFlow?.payload ?? {})}
        demoMode={demoMode}
        onNavigate={onNavigate}
        onClose={closeFlow}
      />
    );
  }

  // Text describe flow (todo 14): analyzeMealText -> structured preview -> review.
  if (kind === "text") {
    return (
      <TextLogFlow
        demoMode={demoMode}
        initialNote={activeFlow?.payload?.note}
        onClose={closeFlow}
      />
    );
  }

  // Voice logging flow (todo 15): record -> transcribe -> editable transcript
  // -> analyzeMealText -> review (source "voice"). The transcript correction
  // step is mandatory here; autoSendVoice applies only inside Chat.
  if (kind === "voice") {
    return <VoiceLogFlow demoMode={demoMode} onClose={closeFlow} />;
  }

  // Nutrition-label scan (todo 18): label photo -> analyze -> correction form
  // -> custom food -> optional review. Handles demo mode internally.
  if (kind === "label") {
    return <LabelScanFlow demoMode={demoMode} onClose={closeFlow} />;
  }

  // Quick add (todo 18): single-item logMealBatch with unknown-preserving
  // calories. Demo mode has no backend, so it opens the fully local review.
  if (kind === "quickAdd") {
    if (demoMode) {
      return (
        <MealReview
          payload={{ items: [], source: "quickAdd" }}
          demoMode={demoMode}
          onNavigate={onNavigate}
          onClose={closeFlow}
        />
      );
    }
    return (
      <FlowSheetShell title="Quick add" onClose={closeFlow} testId="flow-quick-add">
        <MealForm flow="quickAdd" date={localDayStartMs(Date.now())} onClose={closeFlow} />
      </FlowSheetShell>
    );
  }

  // Interim stubs (todos 13-18) + demo-mode exercise/weight. Never dead.
  return <FlowStubSheet kind={kind} onClose={closeFlow} />;
}
